import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/hooks/use-language';
import { useSupabase } from '@/contexts/supabase';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';

export function SignupWindowSettings() {
  const { t } = useLanguage();
  const supabase = useSupabase();
  const [hours, setHours] = useState<number>(72);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    async function fetchSettings() {
      const { data, error } = await supabase
        .from('system_settings')
        .select('signup_hours_before_event')
        .eq('id', 1)
        .single();

      if (!error && data) {
        setHours(data.signup_hours_before_event);
      }
      setFetching(false);
    }
    fetchSettings();
  }, [supabase]);

  const handleSave = async () => {
    setLoading(true);
    const { error } = await supabase
      .from('system_settings')
      .update({ signup_hours_before_event: hours, updated_at: new Date().toISOString() })
      .eq('id', 1);

    if (error) {
      toast.error('Failed to update signup window');
      console.error('Error updating signup window:', error);
    } else {
      toast.success(t('admin.signup_window_updated'));
    }
    setLoading(false);
  };

  if (fetching) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t('admin.signup_window')}
          </CardTitle>
          <CardDescription>{t('admin.signup_window_description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {t('admin.signup_window')}
        </CardTitle>
        <CardDescription>{t('admin.signup_window_description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">
              {t('admin.signup_window_hours')}
            </label>
            <Input
              type="number"
              min="1"
              max="168"
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value) || 72)}
              disabled={loading}
            />
          </div>
          <Button onClick={handleSave} disabled={loading} className="w-full md:w-auto">
            {loading ? 'Saving...' : t('admin.signup_window_save')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Current: {hours} hours ({Math.round(hours / 24)} days) before event
        </p>
      </CardContent>
    </Card>
  );
}
